import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Divider, IconButton, Modal, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import KeepSeparateSvg from '../../../utility/Svg/KeepSeparateISvg';
import LabourSvg from '../../../utility/Svg/LabourSvg';
import PartsSvg from '../../../utility/Svg/PartsSvg';
import styled from '@emotion/styled';

const StyledModeToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',

    padding: '6px',
    borderRadius: '40px',
    background: '#E9E9EA',

    width: 'fit-content',

    '& .MuiToggleButtonGroup-grouped': {
        margin: 0,
        border: 'none !important',
    },

    '& .MuiToggleButton-root': {
        border: 'none !important',
        borderRadius: '24px !important',

        textTransform: 'none',

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',

        padding: '6px 12px',
        minHeight: '36px',

        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',

        color: '#212121',

        background: 'transparent',

        '&:hover': {
            background: '#DCDCDC',
        },

        '&.Mui-selected': {
            background: '#006CDD !important',
            color: '#FFF',
        },

        '&.Mui-selected:hover': {
            background: '#006CDD !important',
        },
    },
}));

/* ---------------- MODES ---------------- */
const MODES = {
    SEPARATE: 'separate',
    LABOUR: 'merge_into_labour',
    PARTS: 'merge_into_parts',
};

const normalizeMode = (val) => {
    if (!val) return MODES.SEPARATE;
    if (val === 'seperate') return MODES.SEPARATE;
    return val;
};

/* ---------------- PREVIEW ---------------- */
const previewMap = {
    [MODES.SEPARATE]: {
        title: 'Preview on PDF (Show as Service Packages)',
        rows: [
            { label: 'Labour', value: '$1,600.00' },
            { label: 'Parts', value: '$4,800.00' },
            { label: 'Service Packages', value: '$800.00', highlight: true },
        ],
    },
    [MODES.LABOUR]: {
        title: 'Preview on PDF (Added into Labour)',
        rows: [
            { label: 'Labour  (Includes Fixed Price Services)', value: '$2,400.00', highlight: true },
            { label: 'Parts', value: '$4,800.00' },
        ],
    },
    [MODES.PARTS]: {
        title: 'Preview on PDF (Added into Parts)',
        rows: [
            { label: 'Labour', value: '$1,600.00' },
            { label: 'Parts (Includes Fixed Price Services)', value: '$5,600.00', highlight: true },
        ],
    },
};

const ServicePackageModal = ({ open, onClose, value, onApply }) => {
    // Internal state so toggling updates the preview without affecting parent until Apply
    const [localValue, setLocalValue] = useState(MODES.SEPARATE);

    // Sync localValue whenever the modal opens or parent value changes
    useEffect(() => {
        if (open) {
            setLocalValue(normalizeMode(value));
        }
    }, [open, value]);

    const preview = previewMap[localValue];

    return (
        <Modal
            open={open}
            onClose={(event, reason) => {
                if (reason === 'backdropClick') {
                    return;
                }
                onClose();
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    width: '700px',
                    boxSizing: 'border-box',

                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    borderRadius: '8px',
                    background: '#FFF',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    outline: 'none',
                    overflow: 'hidden',
                }}
            >
                {/* HEADER */}
                <Box
                    sx={{
                        display: 'flex',
                        height: '64px',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        flexShrink: 0,
                        alignSelf: 'stretch',
                        boxSizing: 'border-box',
                        px: '24px',
                        borderRadius: '8px 8px 0 0!important',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)!important',
                        background: '#FFF',
                        position: 'relative',
                    }}
                >
                    <Typography sx={{ fontSize: 20, fontWeight: 500, color: '#1A1A1A', lineHeight: '32px' }}>
                        Choose How Fixed Price Services Appear in Invoice Totals
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: '16px',
                            display: 'flex',
                            padding: '8px',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: '100px',
                            minWidth: 'unset',
                            width: '40px',
                            height: '40px',
                            color: '#000000',
                        }}
                    >
                        <CloseIcon
                            sx={{
                                fontSize: '24px',
                            }}
                        />
                    </IconButton>
                </Box>

                {/* BODY */}
                <Box
                    sx={{
                        px: '24px',
                        pt: '24px',
                        pb: '24px',
                        width: '100%',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                    }}
                >
                    {/* TOGGLE — driven by localValue */}
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <StyledModeToggleButtonGroup
                            exclusive
                            size="small"
                            value={localValue}
                            onChange={(event, newValue) => {
                                if (newValue && newValue !== localValue) {
                                    setLocalValue(newValue);
                                }
                            }}
                        >
                            {[
                                {
                                    label: 'Show as Service Packages',
                                    value: MODES.SEPARATE,
                                    icon: KeepSeparateSvg,
                                },
                                {
                                    label: 'Merge into Labour',
                                    value: MODES.LABOUR,
                                    icon: LabourSvg,
                                },
                                {
                                    label: 'Merge into Parts',
                                    value: MODES.PARTS,
                                    icon: PartsSvg,
                                },
                            ].map((item) => {
                                const active = localValue === item.value;
                                const Icon = item.icon;

                                return (
                                    <ToggleButton key={item.value} value={item.value} disableRipple>
                                        <Icon active={active} />

                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 500,
                                                lineHeight: '20px',
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                    </ToggleButton>
                                );
                            })}
                        </StyledModeToggleButtonGroup>
                    </Box>

                    {/* PREVIEW SECTION */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            width: '100%',
                        }}
                    >
                        {/* PREVIEW TITLE */}
                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                lineHeight: '20px',
                            }}
                        >
                            {preview?.title}
                        </Typography>

                        {/* PREVIEW CARD */}
                        <Box
                            sx={{
                                display: 'flex',
                                padding: '16px',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: '16px',
                                alignSelf: 'stretch',
                                borderRadius: '4px',
                                border: '1px solid #C4C4C4!important',
                                background: '#FFF',
                            }}
                        >
                            <Stack spacing={2} sx={{ width: '100%' }}>
                                {preview?.rows.map((row) => (
                                    <Box
                                        key={row.label}
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '15px',
                                                fontWeight: 400,
                                                lineHeight: '22px',
                                                color: row.highlight ? '#006CDD' : '#1A1A1A',
                                            }}
                                        >
                                            {row.label}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: '15px',
                                                fontWeight: 400,
                                                lineHeight: '22px',
                                                color: row.highlight ? '#006CDD' : '#1A1A1A',
                                            }}
                                        >
                                            {row.value}
                                        </Typography>
                                    </Box>
                                ))}

                                <Divider sx={{ width: '100%', borderColor: '#E0E0E0', my: 0 }} />

                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography sx={{ fontSize: '15px', fontWeight: 500, color: '#1A1A1A' }}>Subtotal</Typography>

                                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1A1A1A' }}>$7,200.00</Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Box>

                    {/* NOTE */}

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            mt: 0,
                        }}
                    >
                        <InfoOutlinedIcon sx={{ color: '#8C8C8C', fontSize: 18 }} />

                        <Typography sx={{ fontSize: 14, color: '#8C8C8C' }}>
                            This setting only affects how totals are displayed on the PDF. Tax calculations are not affected.
                        </Typography>
                    </Box>
                </Box>

                {/* FOOTER */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        borderRadius: '0 0 8px 8px!important',
                        borderTop: '1px solid rgba(0, 0, 0, 0.12)!important',
                        px: '24px',
                        py: '16px',
                    }}
                >
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={onClose}
                        sx={{
                            minWidth: '90px',
                            height: '40px',
                            px: '10px',
                            py: '4px',
                            background: '#D32F2F',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '15px',
                            fontWeight: 500,
                            lineHeight: '26px',
                            letterSpacing: '0.46px',
                            boxShadow: 'none',

                            '&:hover': {
                                background: '#C62828',
                                border: 'none',
                                boxShadow: 'none',
                            },
                            '&:focus': {
                                border: 'none',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        CANCEL
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => onApply(localValue)}
                        sx={{
                            minWidth: '90px',
                            height: '40px',
                            fontWeight: 700,
                            background: '#006CDD',

                            '&:hover': {
                                background: '#005BB8',
                            },
                        }}
                    >
                        APPLY
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ServicePackageModal;
